import React, { useState, useEffect } from 'react';
import { WalletIcon, CheckCircleIcon, ExclamationCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { walletService } from '../../services/wallet.service';
import { apiRequest } from '../../services/api';
import toast from 'react-hot-toast';

interface WalletPaymentProps {
  amount: number;
  description: string;
  referenceNo: string;
  itemName: string;
  onPaymentCompleted?: () => void;
  onPaymentError?: (error: string) => void;
  disabled?: boolean;
}

interface WalletBalance {
  balance: number;
  currency: string;
  formattedBalance: string;
  isBelowMinimum: boolean;
  isFrozen: boolean;
  isActive: boolean;
  isNewWallet?: boolean;
  pendingDeposit?: { referenceNo: string; amount: number; paymentUrl: string | null } | null;
}

const WalletPayment: React.FC<WalletPaymentProps> = ({
  amount,
  description,
  referenceNo,
  itemName,
  onPaymentCompleted,
  onPaymentError,
  disabled = false
}) => {
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    loadWalletBalance();
  }, []);

  const loadWalletBalance = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const balanceData = await walletService.getBalance();
      setBalance(balanceData);
    } catch (err) {
      console.error('Failed to load wallet balance:', err);
      setError('Unable to load wallet balance');
    } finally {
      setIsLoading(false);
    }
  };

  const hasSufficientBalance = balance && balance.balance >= amount && !balance.isFrozen && balance.isActive;
  const insufficientAmount = balance && amount - balance.balance;

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  const handlePayment = async () => {
    if (!hasSufficientBalance) return;

    setShowConfirmDialog(false);
    setIsProcessing(true);
    setError(null);

    try {
      // Process wallet payment via API
      await apiRequest.post('/wallet/payment', {
        amount,
        description,
        referenceNo,
        itemName,
        type: 'BOOKING_PAYMENT' // Indicate this is for booking payment
      });

      setPaymentSuccess(true);
      toast.success('Payment completed successfully!');

      // Refresh wallet balance
      await loadWalletBalance();

      // Call completion callback
      setTimeout(() => {
        onPaymentCompleted?.();
      }, 1500);

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Payment failed';
      setError(errorMessage);
      toast.error(errorMessage);
      onPaymentError?.(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTopUpWallet = () => {
    // Store current page for return after top-up
    sessionStorage.setItem('returnAfterTopUp', window.location.href);
    // Navigate to wallet page
    window.location.href = '/wallet';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-700">Loading wallet balance...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error && !balance) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-2">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Unable to Load Wallet</h4>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={loadWalletBalance}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Payment Successful!
              </h3>
              <p className="text-sm text-gray-600">
                Your wallet has been charged {formatAmount(amount)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isWalletInactive = balance && (!balance.isActive || balance.isFrozen);

  return (
    <div className="space-y-6">
      {/* Wallet Balance Card */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <WalletIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Wallet Balance
              </h3>
              {balance && (
                <p className="text-2xl font-bold text-purple-600">
                  {balance.formattedBalance}
                </p>
              )}
            </div>
          </div>

          {balance && !isWalletInactive && (
            <button
              onClick={loadWalletBalance}
              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
            >
              Refresh
            </button>
          )}
        </div>

        {/* Payment Amount */}
        <div className="border-t border-purple-200 pt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Payment Amount:</span>
            <span className="text-lg font-bold text-gray-900">
              {formatAmount(amount)}
            </span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Reference No:</span>
            <span className="text-sm text-gray-900 font-mono">
              {referenceNo}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">Payment For:</span>
            <span className="text-sm text-gray-900 max-w-xs truncate">
              {itemName}
            </span>
          </div>
        </div>
      </div>

      {/* Wallet Status */}
      {isWalletInactive && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <ExclamationCircleIcon className="h-5 w-5 text-yellow-500" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-yellow-800">Wallet Unavailable</h4>
              <p className="text-sm text-yellow-700 mt-1">
                {balance?.isFrozen ? 'Your wallet is currently frozen.' : 'Your wallet is not active.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {!hasSufficientBalance && !isWalletInactive && balance && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ExclamationCircleIcon className="h-5 w-5 text-orange-500" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-orange-800">Insufficient Balance</h4>
                <p className="text-sm text-orange-700 mt-1">
                  You need {formatAmount(insufficientAmount)} more to complete this payment.
                </p>
              </div>
            </div>
            <button
              onClick={handleTopUpWallet}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              <span>Top Up Wallet</span>
            </button>
          </div>
        </div>
      )}

      {/* Payment Features */}
      {hasSufficientBalance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-lg">
            <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Instant Payment</h4>
              <p className="text-xs text-gray-600">No redirects needed</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-lg">
            <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">Secure Transaction</h4>
              <p className="text-xs text-gray-600">Encrypted & safe</p>
            </div>
          </div>

          <div className="flex items-center space-x-3 p-4 bg-white border border-gray-200 rounded-lg">
            <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900">No Extra Fees</h4>
              <p className="text-xs text-gray-600">Pay exact amount</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Button */}
      {hasSufficientBalance && !showConfirmDialog && (
        <button
          onClick={() => setShowConfirmDialog(true)}
          disabled={disabled || isProcessing}
          className={`
            w-full py-3 px-6 rounded-lg font-medium text-white transition-all duration-200
            ${disabled || isProcessing
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 active:bg-purple-800 shadow-lg hover:shadow-xl'
            }
          `}
        >
          {isProcessing ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Processing Payment...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <WalletIcon className="h-5 w-5" />
              <span>Pay {formatAmount(amount)} with Wallet</span>
            </div>
          )}
        </button>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && hasSufficientBalance && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-lg">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Confirm Payment</h4>
            <p className="text-sm text-gray-600">
              You're about to pay {formatAmount(amount)} from your wallet for:
            </p>
            <p className="text-sm font-medium text-gray-900 mt-2">{itemName}</p>
          </div>

          <div className="bg-purple-50 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Current Balance:</span>
              <span className="text-sm font-semibold text-gray-900">{balance?.formattedBalance}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Payment Amount:</span>
              <span className="text-sm font-semibold text-red-600">-{formatAmount(amount)}</span>
            </div>
            <div className="border-t border-purple-200 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-900">Remaining Balance:</span>
                <span className="text-sm font-bold text-purple-600">
                  {formatAmount((balance?.balance || 0) - amount)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handlePayment}
              disabled={isProcessing}
              className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : 'Confirm Payment'}
            </button>
            <button
              onClick={() => setShowConfirmDialog(false)}
              disabled={isProcessing}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Trust Badges */}
      <div className="flex justify-center space-x-4 pt-4">
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <span>SSL Secured</span>
        </div>
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <span>Bank-Level Security</span>
        </div>
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
          <span>Instant Processing</span>
        </div>
      </div>
    </div>
  );
};

export default WalletPayment;